import React, { useCallback, useMemo, useState } from 'react';

export const ALL_CATEGORIES = 'ALL';

export interface ICategoryRef {
  code: string | null;
  label: string;
}

export interface ICategoryOption {
  key: string;
  label: string;
}

const keyOf = (category: ICategoryRef | null | undefined): string | null =>
  category ? category.code || category.label || null : null;

/** One option per distinct category on the loaded items, sorted by label; null categories add none. */
export const categoryOptions = <T>(
  items: readonly T[],
  categoryOf: (item: T) => ICategoryRef | null | undefined
): ICategoryOption[] => {
  const options = new Map<string, string>();

  items.forEach(item => {
    const category = categoryOf(item);
    const key = keyOf(category);
    if (key && category && !options.has(key)) options.set(key, category.label || key);
  });

  return Array.from(options, ([key, label]) => ({ key, label })).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
};

/** Items with no category show only under ALL. */
export const useCategoryFilter = <T>(
  items: readonly T[],
  categoryOf: (item: T) => ICategoryRef | null | undefined
) => {
  const [category, setCategory] = useState<string>(ALL_CATEGORIES);
  const options = useMemo(() => categoryOptions(items, categoryOf), [items, categoryOf]);
  const visible = useMemo(
    () =>
      category === ALL_CATEGORIES
        ? items
        : items.filter(item => keyOf(categoryOf(item)) === category),
    [items, category, categoryOf]
  );

  return { category, setCategory, options, visible };
};

/** Selection survives filter changes; Select All acts on the visible set only. */
export const useVisibleSelection = <T>(
  visible: readonly T[],
  idOf: (item: T) => string,
  initial: readonly string[] = []
) => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initial));

  const toggle = useCallback(
    (id: string) =>
      setSelected(current => {
        const next = new Set(current);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      }),
    []
  );

  const add = useCallback(
    (id: string) => setSelected(current => (current.has(id) ? current : new Set(current).add(id))),
    []
  );

  const allVisibleSelected = visible.length > 0 && visible.every(item => selected.has(idOf(item)));

  const toggleAll = () =>
    setSelected(current => {
      const next = new Set(current);
      visible.forEach(item => {
        if (allVisibleSelected) {
          next.delete(idOf(item));
        } else {
          next.add(idOf(item));
        }
      });
      return next;
    });

  return {
    selected,
    isSelected: (id: string) => selected.has(id),
    toggle,
    add,
    allVisibleSelected,
    toggleAll
  };
};

/** Props that make a whole card act as a checkbox. */
export const checkboxCardProps = (checked: boolean, label: string, onToggle: () => void) => ({
  role: 'checkbox' as const,
  'aria-checked': checked,
  'aria-label': label,
  tabIndex: 0,
  onClick: onToggle,
  onKeyDown: (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      onToggle();
    }
  }
});
